import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  debounce,
  ButtonGroup,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import CustomTable from "../components/CustomTable";
import { useNavigate, useParams } from "react-router-dom";
import to from "await-to-js";
import Grid from "@mui/material/Grid2";
import { axiosWithAuth } from "../plugins/axios";
import {
  DataGridPro,
  GridColDef,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnMenuHideItem,
  GridColumnMenuManageItem,
  GridToolbarContainer,
  useGridApiContext,
  GridSortModel,
  GridCellParams,
  getGridStringOperators,
  GridFilterModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { downloadExcelForDataGrid } from "../lib/utils/common";
import { useRole } from "../lib/contexts/RoleContext";
import { getAPIUrlByRole } from "../lib/utils/config";
import Toaster from "../components/Toaster";

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, colDef, open } = props;
  return (
    <GridColumnMenuContainer hideMenu={hideMenu} colDef={colDef} open={open}>
      <GridColumnMenuSortItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuHideItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuManageItem colDef={colDef} onClick={hideMenu} />
    </GridColumnMenuContainer>
  );
}

function CustomNoRowsOverlay({ message }: { message: string }) {
  const isError = message.startsWith("Error");
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Typography color={isError ? "error" : "#000"}>{message}</Typography>
    </Box>
  );
}

function DashboardEvaluationDetails() {
  const [evaluationDetails, setEvaluationDetails] = useState<any>([]);
  const [errMessage, setErrorMessage] = useState<any>(null);
  const [cohortDetails, setCohortDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [answerFile, setAnswerFile] = useState<string | null>(null);
  const [hasNewEvaluated, setHasNewEvaluated] = useState<boolean | null>(null);
  const [selectedRubric, setSelectedRubric] = useState<"old" | "new">("old");
  const [oldEvaluationDetails, setOldEvaluationDetails] = useState<any>([]);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
const [toastOpen, setToastOpen] = useState(false);


  const { courseId, quizId, evaluationId, versionId, assetId } = useParams();
  // const apiUrl = process.env.REACT_APP_PIPELINE_API;
  const role = useRole();
  const apiUrl = getAPIUrlByRole(role);

  const navigate = useNavigate();

  const debouncedFetchCourseDetails = useMemo(
    () =>
      debounce((filter) => {
        fetchCourseDetails(filter);
      }, 1000),
    [sortModel]
  );

  const handleFilterChange = (filterModel: GridFilterModel) => {
    const filters =
      filterModel.items.map((item) => ({
        column: item.field,
        value: item.value,
        operator: item.operator,
      })) || [];
    const filter = {
      // filterColumn: filterModel.items[0]?.field,
      // filterValue: filterModel.items[0]?.value,
      // filterOperator: filterModel.items[0]?.operator,
      filters: JSON.stringify(filters),
    };
    debouncedFetchCourseDetails(filter);
  };
  const fetchCourseDetails = async (filter?: any) => {
    const [err, response] = await to(
      axiosWithAuth(
        `${apiUrl}cohortConfiguration/cohort/${courseId}/quiz/${quizId}`,
        {
          params: {
            sortColumn: sortModel[0]?.field || null,
            sortDirection: sortModel[0]?.sort || null,
            ...filter,
          },
        },
        "get"
      )
    );

    if (err) {
      setError("Failed to load course details");
      setLoading(false);
      return;
    }

    setCohortDetails(response!.data);
  };

  useEffect(() => {
    return () => {
      debouncedFetchCourseDetails.clear();
    };
  }, [debouncedFetchCourseDetails]);

  const isEmpty = (value: any) => !value || value.trim() === "";

  const flattenData = async (data: any) => {
    const result = [] as any;

    data?.forEach((l1: any) => {
      const l1Name = l1.name;
      const l1Comment = isEmpty(l1.comment) ? null : l1.comment;

      if (!l1.children || l1.children.length === 0) {
        result.push({
          ...l1,
          l1Name,
          comment: l1Comment,
          l2Name: "-",
          l3Name: "-",
        });
      } else {
        l1.children.forEach((l2: any) => {
          const l2Name = l2.name;
          const l2Comment = isEmpty(l2.comment) ? l1Comment : l2.comment;

          if (!l2.children || l2.children.length === 0) {
            result.push({
              ...l2,
              l2Name,
              l1Name,
              comment: l2Comment,
              l3Name: "-",
            });
          } else {
            l2.children.forEach((l3: any) => {
              const l3Comment = isEmpty(l3.comment) ? l2Comment : l3.comment;
              result.push({
                ...l3,
                l3Name: l3.name,
                l2Name,
                l1Name,
                comment: l3Comment,
              });
            });
          }
        });
      }
    });

    return result;
  };

  // const flattenData = async (data: any) => {
  //   const result = [] as any;

  //   data?.forEach((l1: any) => {
  //     const l1Name = l1.name;
  //     const comment = l1.comment;

  //     l1.children?.forEach((l2: any) => {
  //       const l2Name = l2.name;

  //       l2.children?.forEach((l3: any) => {
  //         result.push({
  //           ...l3,
  //           l3Name: l3.name,
  //           l2Name,
  //           l1Name,
  //           comment,
  //         });
  //       });
  //     });
  //   });
  //   return result;
  // };

  // function DownloadButton() {
  //     return (
  //         <>
  //             <Button
  //                 onClick={() => {
  //                     downloadExcelForDataGrid({
  //                         rows: evaluationDetails,
  //                         columns: columns,
  //                         fileName: `cohort-${cohortId}-quiz-${quizId}-evaluation-${evaluationId}.xlsx`,
  //                     });
  //                 }}
  //                 variant="outlined"
  //                 color="primary"
  //                 sx={{ marginLeft: "auto", marginBottom: "2rem", display: "flex" }}
  //             >
  //                 Download report
  //             </Button>
  //         </>
  //     );
  // }

  const downloadUrlCache: Record<string, string> = {};
  
  const handleDownloadViaAPI = async (url: string) => {
    if (!url) return;
  
    // If already cached, open directly
    if (downloadUrlCache[url]) {
      window.open(downloadUrlCache[url], "_blank");
      return;
    }
  
    try {
      setDownloadingUrl(url);
  
      const encodedUrl = encodeURIComponent(url);
      const [err, res] = await to(
        axiosWithAuth(
          `${apiUrl}autograde/downloadFileUpload?url=${encodedUrl}`,
          {},
          "get"
        )
      );
  
      if (err) {
        throw new Error("Failed to fetch download link");
      }
  
      const downloadUrl = res.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error("No download URL returned");
      }
  
      // Cache the download URL
      downloadUrlCache[url] = downloadUrl;
  
      window.open(downloadUrl, "_blank");
    } catch (error) {
      console.error("Download error:", error);
      setToastMessage((error as Error).message || "Failed to download file");
      setToastOpen(true);
    } finally {
      setDownloadingUrl(null);
    }
  };
  

  function DownloadButton() {
    return (
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          marginTop: "2rem",
          marginBottom: "2rem",
          borderRadius: 2,
          padding: 3,
        }}
      >
        <Box>
          <ButtonGroup disableElevation sx={{ borderRadius: 0 }}>
            <Button
              variant="contained"
              sx={{
                width: "150px",
                backgroundColor:
                  selectedRubric === "old" ? "#1565c0" : "#e3f2fd",
                color: selectedRubric === "old" ? "#ffffff" : "#000000",
                "&:hover": {
                  backgroundColor:
                    selectedRubric === "old" ? "#0d47a1" : "#bbdefb",
                },
                borderRadius: 0,
              }}
              onClick={() => setSelectedRubric("old")}
            >
              Old Rubrics
            </Button>
            <Button
              variant="contained"
              sx={{
                width: "150px",
                backgroundColor:
                  selectedRubric === "new" ? "#1565c0" : "#e3f2fd",
                color: selectedRubric === "new" ? "#ffffff" : "#000000",
                "&:hover": {
                  backgroundColor:
                    selectedRubric === "new" ? "#0d47a1" : "#bbdefb",
                },
                borderRadius: 0,
              }}
              disabled={!hasNewEvaluated}
              onClick={() => setSelectedRubric("new")}
            >
              New Rubrics
            </Button>
          </ButtonGroup>
        </Box>
        <Box>
          <Button
            onClick={() => {
              downloadExcelForDataGrid({
                rows: evaluationDetails,
                columns: columns,
                fileName: `cohort-${courseId}-quiz-${quizId}-evaluation-${evaluationId}.xlsx`,
              });
            }}
            variant="outlined"
            color="primary"
            sx={{
              marginRight: "1rem",
            }}
          >
            Download report
          </Button>

          {/* {answerFile && (
            <Button
              onClick={() => {
                window.open(answerFile, "_blank");
              }}
              variant="outlined"
              color="primary"
            >
              Download Submission File
            </Button>
          )} */}
          {answerFile && (
  <Button
    onClick={() => {
      handleDownloadViaAPI(answerFile);
    }}
    variant="outlined"
    color="primary"
  >
    Download Submission File
  </Button>
)}
        </Box>
      </Box>
    );
  }

  const cleanString = (str: string): string => {
    return str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  };

  useEffect(() => {
    const fetchEvaluationDetails = async () => {
      const [err, response] = await to(
        axiosWithAuth(
          `${apiUrl}autograde/evaluations/${courseId}/${quizId}/${evaluationId}`,
          {},
          "get"
        )
      );
      console.log({ response });

      if (err) {
        setError("Failed to load evaluation details");
        setLoading(false);
        return;
      }

      setSubmissionId(response.data.submissionId);
      console.log(response.data.submissionId);
      setAnswerFile(response.data?.webhookId?.answer_file);
      setHasNewEvaluated(response.data?.hasNewEvaluated);

      const {
        humanEvaluatedRubrics, //: { children: humanEvaluatedRubrics = [] } = {},
        aiEvaluatedRubrics: aiEvaluatedRubricsData = {},
        oldAiEvaluatedRubrics: oldAiEvaluatedRubricsData = {},
        failureReason,
      } = response.data;

      const humanEvaluatedRubricsChildren =
        humanEvaluatedRubrics?.children ?? [];

      const { children: aiEvaluatedRubrics = [] } =
        aiEvaluatedRubricsData || {};
      const { children: oldAiEvaluatedRubrics = [] } =
        oldAiEvaluatedRubricsData || {};

      let evaluationData, oldEvaluationData;

      if (humanEvaluatedRubricsChildren.length > 0) {
        const [humanErr, humanFlattedData] = await to(
          flattenData(humanEvaluatedRubricsChildren)
        );
        const [aiErr, newAiFlattedData] = await to(
          flattenData(aiEvaluatedRubrics)
        );
        const [oldAiErr, oldAiFlattedData] = await to(
          flattenData(oldAiEvaluatedRubrics)
        );

        if (humanErr || aiErr || oldAiErr) {
          setError("Failed to process evaluation data");
          setLoading(false);
          return;
        }

        // evaluationData = newAiFlattedData.map((l3: any) => {
        //     const humanFeedback = humanFlattedData.find((h: any) => h.id === l3.id);
        //     return {
        //         ...l3,
        //         humanFeedback: humanFeedback ? humanFeedback.comment : "",
        //     };
        // });
        evaluationData = newAiFlattedData.map((l3: any) => {
          const humanFeedback = humanFlattedData.find(
            (h: any) => cleanString(h.name) === cleanString(l3.name)
          );
          return {
            ...l3,
            humanFeedback: humanFeedback
              ? humanFeedback.comment ?? humanFeedback.feedback?.feedback
              : "",
          };
        });
        // oldEvaluationData = oldAiFlattedData.map((l3: any) => {
        //     const humanFeedback = humanFlattedData.find((h: any) => h.id === l3.id);
        //     return {
        //         ...l3,
        //         humanFeedback: humanFeedback ? humanFeedback.comment : "",
        //     };
        // });

        oldEvaluationData = oldAiEvaluatedRubricsData
          ? oldAiFlattedData.map((l3: any) => {
              const humanFeedback = humanFlattedData.find(
                (h: any) => cleanString(h.name) === cleanString(l3.name)
              );
              return {
                ...l3,
                humanFeedback: humanFeedback
                  ? humanFeedback.comment ?? humanFeedback.feedback?.feedback
                  : "",
              };
            })
          : humanFlattedData.map((item: any) => ({
              ...item,
              humanScore: item.score ?? null,
              score: null,
              humanFeedback: item.comment ?? item.feedback?.feedback ?? null,
            }));
        // oldEvaluationData = oldAiFlattedData.map((l3: any) => {
        //     const humanFeedback = humanFlattedData.find(
        //       (h: any) => cleanString(h.name) === cleanString(l3.name)
        //     );
        //     return {
        //       ...l3,
        //       humanFeedback: humanFeedback ? humanFeedback.comment : "",
        //     };
        //   });
      } else {
        const [aiErr, newAiFlattedData] = await to(
          flattenData(aiEvaluatedRubrics)
        );
        const [oldAiErr, oldAiFlattedData] = await to(
          flattenData(oldAiEvaluatedRubrics)
        );

        if (aiErr || oldAiErr) {
          setError("Failed to process evaluation data");
          setLoading(false);
          return;
        }

        evaluationData = newAiFlattedData;
        oldEvaluationData = oldAiFlattedData;
      }

      evaluationData = evaluationData.map((item: any) => ({
        ...item,
        gradingGuideline:
          item.gradingGuidelines || item.gradingGuideline || "-",
      }));

      oldEvaluationData = oldEvaluationData.map((item: any) => ({
        ...item,
        gradingGuideline:
          item.gradingGuidelines || item.gradingGuideline || "-",
      }));
      // console.log(evaluationData)

      // setEvaluationDetails(evaluationData);
      // setOldEvaluationDetails(oldEvaluationData);
      setEvaluationDetails(
        evaluationData.sort((a: any, b: any) => a.id - b.id)
      );
      setOldEvaluationDetails(
        oldEvaluationData.sort((a: any, b: any) => a.id - b.id)
      );
      setLoading(false);

      setErrorMessage(failureReason);
    };

    fetchEvaluationDetails();
    fetchCourseDetails();
  }, [apiUrl, page, rowsPerPage, sortModel]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  const renderStyledCell = (params: GridCellParams) => (
    <div
      style={{
        maxHeight: "70px",
        overflowY: "auto",
        lineHeight: "1.1rem",
        whiteSpace: "normal",
        wordWrap: "break-word",
        display: "block",
        WebkitLineClamp: 5,
        WebkitBoxOrient: "vertical",
        textAlign: "center",
        justifyContent: "center",
      }}
      dangerouslySetInnerHTML={{ __html: params.value as string }}
    ></div>
  );

  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Rubric ID",
      width: 100,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "l1Name",
      headerName: "L1",
      flex: 1,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <span className="whitespace-normal">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "l2Name",
      headerName: "L2",
      flex: 1,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <span className="whitespace-normal">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "l3Name",
      headerName: "L3",
      flex: 1,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <span className="whitespace-normal">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "gradingGuideline",
      headerName: "Grading Guidelines",
      flex: 2,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        // const value = params.row?.gradingGuidelines || params.row?.gradingGuideline;

        return (
          <span className="whitespace-normal">
            {params.value ? (
              <div dangerouslySetInnerHTML={{ __html: params.value }} />
            ) : (
              "-"
            )}
          </span>
        );
      },
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "maxMarks",
      headerName: "Total Marks",
      flex: 1,
      renderHeader: (params) => (
        <Box
          sx={{
            display: "flex",
            whiteSpace: "normal",
            alignItems: "center",
          }}
        >
          {params.colDef.headerName}
        </Box>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "humanScore",
      headerName: "Manual Grader Marks",
      flex: 1,
      renderHeader: (params) => (
        <Box
          sx={{
            display: "flex",
            whiteSpace: "normal",
            alignItems: "center",
          }}
        >
          {params.colDef.headerName}
        </Box>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "score",
      headerName: "Autograder Marks",
      flex: 1,
      renderHeader: (params) => (
        <Box
          sx={{
            display: "flex",
            whiteSpace: "normal",
            alignItems: "center",
          }}
        >
          {params.colDef.headerName}
        </Box>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "deviation",
      headerName: "Deviation",
      flex: 1,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      filterOperators: getCustomFilterOperators(),
      renderCell: (params: GridRenderCellParams) => {
        const humanScore = params.row?.humanScore ?? 0;
        const aiScore = params.row?.score ?? 0;

        if (humanScore === 0 && aiScore === 0) {
          return "0%";
        }
        let val: string | undefined = params.row.deviation;

        if (val) {
          val = val.slice(0, -1);
          val = `${Math.round(Number(val))}%`;
        }
        return val || "-";
      },
    },

    {
      field: "humanFeedback",
      headerName: "Manual Grader Comments",
      flex: 2,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <Box className="whitespace-normal flex justify-center">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </Box>
      ),
      filterOperators: getCustomFilterOperators(),
      cellClassName: "manual-grader-comments-cell",
    },
    {
      field: "feedback",
      headerName: "Autograder Comments",
      flex: 2,
      renderHeader: (params) => (
        <Box
          sx={{
            display: "flex",
            whiteSpace: "normal",
          }}
        >
          {params.colDef.headerName}
        </Box>
      ),
      renderCell: (params) => (
        <span className="whitespace-normal">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "evidence",
      headerName: "Autograder Evidence",
      flex: 2,
      renderHeader: (params) => (
        <span className="whitespace-normal">{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <span className="whitespace-normal">
          {params.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: params.value,
              }}
            />
          ) : (
            "-"
          )}
        </span>
      ),
      filterOperators: getCustomFilterOperators(),
    },
  ];

  function CustomNoRowsOverlay({ message }: { message: string }) {
    const isError = message.startsWith("Error");
    return (
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          padding: 1,
          boxSizing: "border-box",
          backgroundColor: isError ? "#ffeeee" : "transparent",
          border: isError ? "1px solid #ffcccc" : "none",
        }}
      >
        <Typography
          color={isError ? "error" : "#000"}
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={4}>
      <Box pb={4}>
        <Grid container spacing={2}>
          <Grid size={4}>
            <Typography gutterBottom>
              Cohort:
              <Typography display={"inline"} fontWeight={"bold"}>
                {courseId}
              </Typography>
            </Typography>
            <Typography fontWeight={"bold"}>
              {cohortDetails.cohortName}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography gutterBottom>
              Quiz:
              <Typography display={"inline"} fontWeight={"bold"}>
                {quizId}
              </Typography>
            </Typography>
            <Typography gutterBottom fontWeight={"bold"}>
              {cohortDetails.quizName}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography gutterBottom>
              Evaluation ID:
              <Typography display={"inline"} fontWeight={"bold"}>
                {submissionId}
              </Typography>
            </Typography>
            {role === "prism" && (
              <Typography gutterBottom>
                Version:
                <Typography display={"inline"} fontWeight={"bold"}>
                  {versionId}
                </Typography>
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <Typography variant="h4" gutterBottom>
        Evaluation Details
      </Typography>
      {/* <Box justifyContent={"right"} display={"flex"}>
                <Button>Run / Re Run Evaluations</Button>
            </Box> */}
      <Box>
        <DownloadButton />
        <DataGridPro
          rows={
            errMessage && !hasNewEvaluated && evaluationDetails.length === 0
              ? []
              : hasNewEvaluated
              ? selectedRubric === "old"
                ? oldEvaluationDetails
                : evaluationDetails
              : evaluationDetails.length > 0
              ? evaluationDetails
              : oldEvaluationDetails
          }
          columns={columns.map((column) => ({
            ...column,
            align: "left",
            headerAlign: "center",
          }))}
          autoHeight
          hideFooter
          getRowId={(row) => `${row.id}-${row.name}`}
          slots={{
            // columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <CustomNoRowsOverlay
                message={
                  errMessage ? `Error: ${errMessage}` : "No data available"
                }
              />
            ),
          }}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          getRowHeight={() => "auto"}
          sx={{
            mb: 3,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "!flex-start",
              whiteSpace: "normal",
              paddingTop: "10px",
              paddingBottom: "10px",
            },
            "& .MuiDataGrid-columnHeader": {
              fontSize: "14px !important",
              fontWeight: "800 !important",
            },
          }}
          onFilterModelChange={handleFilterChange}
        />
      </Box>

      <Snackbar
      open={toastOpen}
      autoHideDuration={4000}
      onClose={() => setToastOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert onClose={() => setToastOpen(false)} severity="error" sx={{ width: "100%" }}>
        {toastMessage}
      </Alert>
    </Snackbar>

    </Box>
  );
}
export default DashboardEvaluationDetails;
